import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Stack, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import useCreateHotelExtra from "../../hooks/hotelExtras/useCreateHotelExtra";
import useUpdateHotelExtra from "../../hooks/hotelExtras/useUpdateHotelExtra";
import FormInput from "../inputs/FormInput";
import i18next from "i18next";
import LanguageToggle from "../global/LanguageToggle";

const pricingBasisOptions = [
    { id: 'once', name: { ar: 'مرة واحدة', en: 'Once' } },
    { id: 'per_night', name: { ar: 'كل ليلة', en: 'Per Night' } },
    { id: 'per_room', name: { ar: 'كل غرفة', en: 'Per Room' } },
    { id: 'room_night', name: { ar: 'غرفة/ليلة', en: 'Room/Night' } },
    { id: 'per_person', name: { ar: 'للشخص', en: 'Per Person' } },
];

const FormAddEditHotelExtra = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    const showArabic = activeLang === "ar";
    const showEnglish = activeLang === "en";

    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [nameAr, setNameAr] = useState(currentData?.name_ar || "");
    const [nameEn, setNameEn] = useState(currentData?.name_en || "");
    const [defaultSellPrice, setDefaultSellPrice] = useState(currentData?.default_sell_price || 0);
    const [defaultCostPrice, setDefaultCostPrice] = useState(currentData?.default_cost_price || 0);
    const [defaultPricingBasis, setDefaultPricingBasis] = useState(currentData?.default_pricing_basis || "once");
    const [isActive, setIsActive] = useState(currentData?.is_active ?? true);

    const { createHotelExtra, isPending: isCreating } = useCreateHotelExtra();
    const { editHotelExtra, isUpdate } = useUpdateHotelExtra();

    const loadingSubmit = isCreating || isUpdate;

    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setDefaultSellPrice(currentData?.default_sell_price ?? 0);
            setDefaultCostPrice(currentData?.default_cost_price ?? 0);
            setDefaultPricingBasis(currentData?.default_pricing_basis || "once");
            setIsActive(currentData?.is_active ?? true);
        }
    }, [isEdit, currentData]);

    const SubmitForm = (e) => {
        e.preventDefault();
        const payload = {
            name: {
                ar: nameAr || nameEn,
                en: nameEn || nameAr,
            },
            default_sell_price: parseFloat(defaultSellPrice) || 0,
            default_cost_price: parseFloat(defaultCostPrice) || 0,
            default_pricing_basis: defaultPricingBasis,
            is_active: isActive,
        };
        if (isEdit && currentData?.id) {
            editHotelExtra(
                { id: currentData.id, req: payload },
                {
                    onSuccess: () => {
                        if (onSubmitSuccess) onSubmitSuccess();
                    },
                }
            );
        } else {
            createHotelExtra(payload, {
                onSuccess: () => {
                    if (onSubmitSuccess) onSubmitSuccess();
                },
            });
        }
    };

    const statusOptions = [
        { id: true, name: isArabic ? "نشط" : "Active" },
        { id: false, name: isArabic ? "معطل" : "Disabled" },
    ];

    const isActiveValue = isActive === true || isActive === "true" || isActive === 1;

    const pricingBasisOptionsLocal = pricingBasisOptions.map(opt => ({
        id: opt.id,
        name: isArabic ? opt.name.ar : opt.name.en,
    }));

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box
                component="form"
                onSubmit={SubmitForm}
                sx={{
                    p: { xs: 2, md: 4 },
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Typography variant="h6">
                        {isEdit
                            ? isArabic
                                ? "تعديل إضافة فندق"
                                : "Edit Hotel Extra"
                            : isArabic
                            ? "إضافة إضافة فندق جديدة"
                            : "Create New Hotel Extra"}
                    </Typography>

                    <LanguageToggle
                        activeLang={activeLang}
                        onChange={(lng) => setActiveLang(lng)}
                        showLabel={true}
                    />
                </Box>

                <Box mb={3}>
                    <Grid container spacing={2}>
                        {showArabic && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="الاسم (عربي)"
                                    placeholder="أدخل اسم الإضافة بالعربي"
                                    type="text"
                                    name="name_ar"
                                    value={nameAr}
                                    onChange={(e) => setNameAr(e.target.value)}
                                    required
                                />
                            </Grid>
                        )}

                        {showEnglish && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="Name (English)"
                                    placeholder="Enter hotel extra name in English"
                                    type="text"
                                    name="name_en"
                                    value={nameEn}
                                    onChange={(e) => setNameEn(e.target.value)}
                                    required
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "سعر البيع الافتراضي" : "Default Sell Price"}
                                type="number"
                                name="default_sell_price"
                                value={defaultSellPrice}
                                onChange={(e) => setDefaultSellPrice(e.target.value)}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "سعر التكلفة الافتراضي" : "Default Cost Price"}
                                type="number"
                                name="default_cost_price"
                                value={defaultCostPrice}
                                onChange={(e) => setDefaultCostPrice(e.target.value)}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "أساس التسعير" : "Pricing Basis"}
                                type="select"
                                name="default_pricing_basis"
                                value={defaultPricingBasis}
                                onChange={(e) => setDefaultPricingBasis(e.target.value)}
                                options={pricingBasisOptionsLocal}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "الحالة" : "Status"}
                                type="select"
                                name="is_active"
                                value={isActiveValue}
                                onChange={(e) => setIsActive(e.target.value === true || e.target.value === "true" || e.target.value === 1)}
                                options={statusOptions}
                                required
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Stack direction="row" spacing={1}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loadingSubmit}
                        >
                            {loadingSubmit
                                ? isArabic
                                    ? "جارٍ الحفظ..."
                                    : "Saving..."
                                : isEdit
                                ? isArabic
                                    ? "تحديث"
                                    : "Update"
                                : isArabic
                                ? "حفظ"
                                : "Save"}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditHotelExtra;
